import React, { useState, useCallback, useEffect } from 'react'
import {
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from '@you/ui'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@you/ui'
import IconSelector from './IconSelector'
import { ICON_OPTIONS } from './icon-options'

/**
 * 数据集表单数据
 */
interface DatasetForm {
  name: string
  desc: string
  icon: string
}

/**
 * 对话框模式
 */
type DialogMode = 'create' | 'edit'

/**
 * 数据集对话框组件属性
 */
interface UpdateDatasetDialogProps {
  /** 对话框是否打开 */
  open: boolean
  /** 对话框打开状态变化回调 */
  onOpenChange: (open: boolean) => void
  /** 对话框模式：创建或编辑 */
  mode?: DialogMode
  /** 初始数据（编辑模式时使用） */
  initialData?: DatasetForm
  /** 提交回调 */
  onSubmit: (data: DatasetForm) => Promise<void>
  /** 是否正在提交中 */
  isSubmitting?: boolean
  /** 创建并导入数据集回调（仅创建模式） */
  onCreateAndImport?: (data: DatasetForm) => Promise<void>
}

/**
 * 数据集对话框组件
 */
const UpdateDatasetDialog: React.FC<UpdateDatasetDialogProps> = ({
  open,
  onOpenChange,
  mode = 'create',
  initialData,
  onSubmit,
  isSubmitting = false,
  onCreateAndImport,
}) => {
  // 表单状态
  const [form, setForm] = useState<DatasetForm>({
    name: '',
    desc: '',
    icon: ICON_OPTIONS[0].value,
  })

  // 当对话框打开且为编辑模式时，初始化表单数据
  useEffect(() => {
    if (open && mode === 'edit' && initialData) {
      setForm(initialData)
    } else if (open && mode === 'create') {
      // 创建模式时重置表单
      setForm({
        name: '',
        desc: '',
        icon: ICON_OPTIONS[0].value,
      })
    }
  }, [open, mode, initialData])

  /**
   * 处理表单字段变化
   */
  const handleFormChange = useCallback(
    (field: keyof DatasetForm, value: string) => {
      setForm((prev) => ({
        ...prev,
        [field]: value,
      }))
    },
    []
  )

  /**
   * 处理图标选择
   */
  const handleIconSelect = useCallback((iconColor: string) => {
    setForm((prev) => ({
      ...prev,
      icon: iconColor,
    }))
  }, [])

  /**
   * 重置表单
   */
  const resetForm = useCallback(() => {
    setForm({
      name: '',
      desc: '',
      icon: ICON_OPTIONS[0].value,
    })
  }, [])

  /**
   * 处理提交
   */
  const handleSubmit = useCallback(
    async (andImport: boolean = false) => {
      if (!form.name.trim()) {
        toast.error('请输入数据集名称')
        return
      }
      if (!form.desc.trim()) {
        toast.error('请输入数据集描述')
        return
      }

      try {
        const datasetData = {
          name: form.name.trim(),
          desc: form.desc.trim(),
          icon: form.icon,
        }

        if (mode === 'create' && andImport && onCreateAndImport) {
          await onCreateAndImport(datasetData)
        } else {
          await onSubmit(datasetData)
        }

        // 成功后重置表单并关闭对话框
        resetForm()
        onOpenChange(false)
      } catch (error) {
        // 错误处理由父组件负责，这里不需要额外处理
        console.error(
          `${mode === 'create' ? '创建' : '更新'}数据集失败:`,
          error
        )
      }
    },
    [form, mode, onSubmit, onCreateAndImport, resetForm, onOpenChange]
  )

  /**
   * 处理取消操作
   */
  const handleCancel = useCallback(() => {
    resetForm()
    onOpenChange(false)
  }, [resetForm, onOpenChange])

  /**
   * 处理对话框状态变化
   */
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        resetForm()
      }
      onOpenChange(newOpen)
    },
    [resetForm, onOpenChange]
  )

  const isCreateMode = mode === 'create'
  const dialogTitle = isCreateMode ? '创建数据集' : '编辑数据集'
  const submitButtonText = isSubmitting
    ? isCreateMode
      ? '创建中...'
      : '更新中...'
    : isCreateMode
    ? '完成创建'
    : '保存修改'

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[587px]">
        <DialogHeader title={dialogTitle} />

        <div className="space-y-6 p-4">
          {/* 数据集名称 */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              <span className="text-red-500">*</span> 名称
            </label>
            <div className="relative">
              <Input
                value={form.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                placeholder="请输入数据集名称"
                maxLength={100}
                className="pr-16"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
                {form.name.length}/100
              </span>
            </div>
          </div>

          {/* 数据集描述 */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              <span className="text-red-500">*</span> 描述
            </label>
            <div className="relative">
              <Textarea
                value={form.desc}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  handleFormChange('desc', e.target.value)
                }
                placeholder="请输入数据集描述"
                maxLength={100}
                rows={3}
                className="resize-none pr-16"
              />
              <span className="absolute right-3 bottom-3 text-xs text-gray-400">
                {form.desc.length}/100
              </span>
            </div>
          </div>

          {/* 数据集图标 */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              选择图标
            </label>
            <IconSelector
              selectedIcon={form.icon}
              onIconSelect={handleIconSelect}
            />
          </div>
        </div>

        <DialogFooter className="space-x-3 justify-end">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            取消
          </Button>
          <Button
            onClick={() => handleSubmit(isCreateMode)}
            variant={isCreateMode ? 'outline' : 'default'}
            disabled={isSubmitting}
          >
            {submitButtonText}
          </Button>
          {isCreateMode && onCreateAndImport && (
            <Button onClick={() => handleSubmit(true)} disabled={isSubmitting}>
              {isSubmitting ? '创建中...' : '创建并导入'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default UpdateDatasetDialog
export type { DatasetForm }
